import { EventAggregator } from 'aurelia-event-aggregator';
import { autoinject, bindable, bindingMode } from 'aurelia-framework';
import { Router } from 'aurelia-router';
import * as moment from 'moment';
import { Payment } from '../../models/customer';
import { PaymentHistory } from '../../models/payment-history';
import { State } from '../../services/global-state';
import { PayrazrService } from '../../services/payrazr';
import { PaymentItemType } from '../../smart/models/enums/payment-item-type';
import { PaymentItem } from '../../smart/models/payment-item';
import { PaymentItemTableConfiguration } from '../../smart/models/payment-item-table-configuration';

@autoinject
export class CustomerDashboard {
	globalState: State;
	selectedAccounts: any;
	isLoading: boolean = false;
	isShoppingCartEnabled: boolean = false;

	// payment item selection properties
	@bindable paymentItemTableConfiguration: PaymentItemTableConfiguration;
	@bindable businessKey: number;
	@bindable customerAccountKeys: number[];
	@bindable forceRefreshOfData: boolean;

	// dashboard summary properties
	@bindable({ defaultBindingMode: bindingMode.twoWay }) summaryTotalBalance: number;
	@bindable({ defaultBindingMode: bindingMode.twoWay }) summaryLastPaymentDate: Date | null;
	@bindable({ defaultBindingMode: bindingMode.twoWay }) summaryLastPaymentAmount: number;
	@bindable({ defaultBindingMode: bindingMode.twoWay }) summaryNextPaymentDate: Date | null;

	constructor(
		private payrazrService: PayrazrService,
		private eventAggregator: EventAggregator,
		private router: Router,
		state: State
	) {
		this.globalState = state;
	}

	activate(params: any) {
		this.isLoading = true;
		this.isShoppingCartEnabled = this.globalState.business.portalSettings.businessSettings.isShoppingCartEnabled;

		// this default value may change in the future if cases come up where we don't want to reload data on the dashboard
		this.forceRefreshOfData = true;

		// Bail out of refreshing customer if we have only just logged in.
		if (this.globalState.isLoggingIn) {
			this.globalState.isLoggingIn = false;
			this.loadPaymentItems();
			return;
		}

		this.globalState = null;
		this.refresh();
		this.eventAggregator.subscribe('refresh-global-state', message => {
			this.router.refreshNavigation();
		});
	}

	refresh() {
		this.isLoading = true;
		this.payrazrService.refreshGlobalCustomer()
			// this call in here in dashboard keeps the globalstate fresh in the Portal from user actions performed on children views by user
			// ... references of globalState from children views get the globalState from local storage.
			.then((globalState: State) => {
				this.globalState = globalState;
				this.loadPaymentItems();
			});
	}

	loadPaymentItems() {
		// the new payment item selection shared component supports both invoices/accounts, but at this time in the portal refresh
		// we are just attacking accounts, so for now only worry about wiring up the accounts piece. In the future, add a check to
		// see if the business is doing invoice or account level payments.
		this.paymentItemTableConfiguration = new PaymentItemTableConfiguration();
		this.paymentItemTableConfiguration.itemType = PaymentItemType.Account;
		this.paymentItemTableConfiguration.selectFirstItemUponLoad = true;
		this.paymentItemTableConfiguration.showBucket = true;
		this.paymentItemTableConfiguration.useMultiselect = true;
		this.paymentItemTableConfiguration.isGuarantorBillingEnabled = this.globalState.isConsumerPortalGuarantorBillingEnabled;

		this.businessKey = this.globalState.business.key;

		this.customerAccountKeys = this.globalState.accounts.filter(item => {
			let portfolio = this.globalState.business.fetchPortfolioSettings(item.portfolioName);
			if (!portfolio || !portfolio.hasSettings || !portfolio.settings.paymentMethods) {
				return false;
			}
			return true;
		}).map(x => x.customerAccountKey);
	}

	selectedPaymentItemsChanged(selectedPaymentItems: PaymentItem[]) {
		// bind accounts to the payment cards based on selected payment items
		let selectedPaymentItemCustomerAccountKeys: number[] = selectedPaymentItems
			.filter((i: PaymentItem) => i.isSelected)
			.map(x => x.itemKey);

		if (selectedPaymentItemCustomerAccountKeys.length > 0) {
			this.selectedAccounts = this.globalState.accounts.filter(
				function(account: any) {
					return this.indexOf(account.customerAccountKey) >= 0;
				},
				selectedPaymentItemCustomerAccountKeys
			);
		} else {
			this.selectedAccounts = null;
		}
	}

	paymentItemsLoaded(paymentItems: PaymentItem[]) {
		this.loadDashboardSummaryInfo(paymentItems);
	}

	loadDashboardSummaryInfo(paymentItems: PaymentItem[]) {
		this.summaryTotalBalance = paymentItems
			.map(x => x.initialAmount)
			.reduce((sum, currentPaymentAmount) => sum + currentPaymentAmount, 0);

		// old portal code to load recent payment info for dashboard summary. Would like to refactor this to clean it up...
		if (this.globalState && this.globalState.customer && this.globalState.accounts) {
			this.payrazrService.getPaymentHistory()
				.then((pastPayments: PaymentHistory[]) => {
					this.globalState.payments = pastPayments.map(payment => new Payment(payment));
					let recentPayments: any = this.globalState.payments;
					let recentPaymentsLastPaymentDate: any = null;
					if (recentPayments.length !== 0) {
						recentPayments.forEach((item: Payment) => {
							if (item.result === 'Approved' || item.result === 'Declined' || item.result === 'Error' || item.result === 'Submitted' || item.result === 'Returned') {
								let m: moment.Moment = moment(item.paymentDate);
								if (!recentPaymentsLastPaymentDate || (recentPaymentsLastPaymentDate && recentPaymentsLastPaymentDate < m)) {
									this.summaryLastPaymentDate = m.toDate();
									this.summaryLastPaymentAmount = item.paymentAmount;
								}
							}
						});
					}
					this.isLoading = false;
				})
				.catch(error => {
					this.isLoading = false;
					alert('error loading recent payments');
				});
		} else {
			this.isLoading = false; // Just in case, prevents infinite spinner
		}

		// old portal code to load next payment info for dashboard summary. Would like to refactor this to clean it up...
		let scheduledPayments: any = [];
		if (this.globalState.accounts) {
			this.globalState.accounts.forEach(account => {
				if (account.paymentPlans) {
					account.paymentPlans.forEach(paymentPlan => {
						scheduledPayments = scheduledPayments.concat(paymentPlan.payments.map(payment => {
							return new DashboardScheduledPayment(payment, account.displayName, paymentPlan.paymentInstrument);
						}));
					});
				}
			});
		}
		scheduledPayments = scheduledPayments
			.sort((a: DashboardScheduledPayment, b: DashboardScheduledPayment) => {
				const aDate = moment(a.paymentDate);
				const bDate = moment(b.paymentDate);

				if (aDate.isBefore(bDate)) {
					return -1;
				} else if (aDate.isSame(bDate)) {
					return 0;
				} else {
					return 1;
				}

			})
			.slice(0, 6);

		if (scheduledPayments.length > 0) {
			this.summaryNextPaymentDate = moment(scheduledPayments[0].paymentDate).toDate();
		}
	}
}

export class DashboardScheduledPayment {
	paymentDate: moment.Moment;
	accountName: any;
	paymentAmount: any;
	paymentMethodType: any;
	paymentMethodAccount: any;

	constructor(plannedPayment, account, instrument) {
		this.paymentDate = moment(plannedPayment.paymentDate);
		this.accountName = account;
		this.paymentAmount = plannedPayment.paymentAmount;
		this.paymentMethodType = instrument.paymentType;
		this.paymentMethodAccount = instrument.paymentAccount;
	}
}